import { useEffect, useState } from 'react';
import { useRoute, Link } from 'wouter';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  useWorkOrderDetail,
  useWorkOrderUpdate,
  useWorkOrderTransition,
  WORK_ORDER_STATUS_LABELS,
  WORK_ORDER_PRIORITY_LABELS,
  WORK_ORDER_TYPE_LABELS,
  type ServiceWorkOrderDto,
} from '@/hooks/useSwo';
import { useAuth } from '@/hooks/useAuth';
import AccessDenied from '@/components/AccessDenied';
import { SWO_PERMISSIONS, hasPermission } from '@/lib/swoPermissions';

const STATUS_BADGE: Record<string, string> = {
  open: 'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-blue-100 text-blue-700',
  blocked: 'bg-red-100 text-red-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

const PRIORITY_BADGE: Record<string, string> = {
  low: 'bg-slate-100 text-slate-700',
  normal: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

export default function WorkOrderDetailPage() {
  const [, params] = useRoute('/operations/work-orders/:id');
  const id = params?.id ?? '';

  const { permissions } = useAuth();
  const { loading: detailLoading, error: detailError, fetch: fetchDetail } = useWorkOrderDetail();
  const { loading: updateLoading, error: updateError, update } = useWorkOrderUpdate();
  const {
    loading: transLoading,
    error: transError,
    start,
    block,
    unblock,
    complete,
    cancel,
    assign,
  } = useWorkOrderTransition();

  const [workOrder, setWorkOrder] = useState<ServiceWorkOrderDto | null>(null);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editPriority, setEditPriority] = useState('');
  const [editDueAt, setEditDueAt] = useState('');

  // Transition dialog state
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assigneeId, setAssigneeId] = useState('');

  const canRead = hasPermission(permissions, SWO_PERMISSIONS.WORK_ORDER_READ);
  const canUpdate = hasPermission(permissions, SWO_PERMISSIONS.WORK_ORDER_UPDATE);
  const canAssign = hasPermission(permissions, SWO_PERMISSIONS.WORK_ORDER_ASSIGN);
  const canTransition = hasPermission(permissions, SWO_PERMISSIONS.WORK_ORDER_TRANSITION);
  const canCancel = hasPermission(permissions, SWO_PERMISSIONS.WORK_ORDER_CANCEL);

  useEffect(() => {
    if (!canRead || !id) return;
    void (async () => {
      const result = await fetchDetail(id);
      if (result) {
        setWorkOrder(result);
      }
    })();
  }, [canRead, fetchDetail, id]);

  useEffect(() => {
    if (detailError) toast.error(`加载工单失败：${detailError.message}`);
    if (updateError) toast.error(`更新工单失败：${updateError.message}`);
    if (transError) toast.error(`操作失败：${transError.message}`);
  }, [detailError, updateError, transError]);

  if (!canRead) return <AccessDenied resource="服务工单" />;
  if (detailLoading) return <div className="p-8 text-center text-sm text-muted-foreground">加载中…</div>;
  if (!workOrder) return <div className="p-8 text-center text-sm text-muted-foreground">工单不存在</div>;

  const isTerminal = workOrder.status === 'completed' || workOrder.status === 'cancelled';

  function startEdit() {
    if (!workOrder) return;
    setEditTitle(workOrder.title);
    setEditDescription(workOrder.description ?? '');
    setEditPriority(workOrder.priority);
    setEditDueAt(workOrder.dueAt ? new Date(workOrder.dueAt).toISOString().slice(0, 10) : '');
    setIsEditing(true);
  }

  async function handleSave() {
    if (!workOrder) return;
    const updated = await update(workOrder.id, {
      expectedVersion: workOrder.version,
      title: editTitle,
      description: editDescription,
      priority: editPriority,
      dueAt: editDueAt || undefined,
    });
    if (updated) {
      setWorkOrder(updated);
      setIsEditing(false);
      toast.success('工单已更新');
    }
  }

  async function handleStart() {
    if (!workOrder) return;
    const updated = await start(workOrder.id, workOrder.version);
    if (updated) {
      setWorkOrder(updated);
      toast.success('工单已开始');
    }
  }

  async function handleBlock() {
    if (!workOrder || !blockReason.trim()) return;
    const updated = await block(workOrder.id, {
      expectedVersion: workOrder.version,
      blockReason: blockReason.trim(),
    });
    if (updated) {
      setWorkOrder(updated);
      setShowBlockDialog(false);
      setBlockReason('');
      toast.success('工单已阻塞');
    }
  }

  async function handleUnblock() {
    if (!workOrder) return;
    const updated = await unblock(workOrder.id, workOrder.version);
    if (updated) {
      setWorkOrder(updated);
      toast.success('工单已解除阻塞');
    }
  }

  async function handleComplete() {
    if (!workOrder) return;
    const updated = await complete(workOrder.id, workOrder.version);
    if (updated) {
      setWorkOrder(updated);
      toast.success('工单已完成');
    }
  }

  async function handleCancel() {
    if (!workOrder || !cancelReason.trim()) return;
    const updated = await cancel(workOrder.id, {
      expectedVersion: workOrder.version,
      cancelReason: cancelReason.trim(),
    });
    if (updated) {
      setWorkOrder(updated);
      setShowCancelDialog(false);
      setCancelReason('');
      toast.success('工单已取消');
    }
  }

  async function handleAssign() {
    if (!workOrder || !assigneeId.trim()) return;
    const updated = await assign(workOrder.id, {
      expectedVersion: workOrder.version,
      assigneeId: assigneeId.trim(),
    });
    if (updated) {
      setWorkOrder(updated);
      setShowAssignDialog(false);
      setAssigneeId('');
      toast.success('工单已指派');
    }
  }

  const actionLoading = updateLoading || transLoading;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Link href="/operations/work-orders" className="hover:underline">
          服务工单
        </Link>
        <span>/</span>
        <span className="text-foreground">{workOrder.title}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold">{workOrder.title}</h2>
          <div className="mt-1 flex items-center gap-2">
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[workOrder.status] ?? ''}`}
            >
              {WORK_ORDER_STATUS_LABELS[workOrder.status] ?? workOrder.status}
            </span>
            <span
              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${PRIORITY_BADGE[workOrder.priority] ?? ''}`}
            >
              {WORK_ORDER_PRIORITY_LABELS[workOrder.priority] ?? workOrder.priority}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        {!isTerminal && (
          <div className="flex flex-wrap gap-2">
            {canUpdate && !isEditing && (
              <Button variant="outline" size="sm" onClick={startEdit}>
                编辑
              </Button>
            )}
            {canAssign && !isEditing && (
              <Button variant="outline" size="sm" onClick={() => setShowAssignDialog(true)}>
                指派
              </Button>
            )}
            {canTransition && workOrder.status === 'open' && (
              <Button size="sm" onClick={handleStart} disabled={actionLoading}>
                开始
              </Button>
            )}
            {canTransition && workOrder.status === 'in_progress' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBlockDialog(true)}
                  disabled={actionLoading}
                >
                  阻塞
                </Button>
                <Button size="sm" onClick={handleComplete} disabled={actionLoading}>
                  完成
                </Button>
              </>
            )}
            {canTransition && workOrder.status === 'blocked' && (
              <Button size="sm" onClick={handleUnblock} disabled={actionLoading}>
                解除阻塞
              </Button>
            )}
            {canCancel && !isTerminal && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowCancelDialog(true)}
                disabled={actionLoading}
              >
                取消
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Detail card */}
      <Card className="p-6">
        {isEditing ? (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium">标题</label>
              <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">描述</label>
              <textarea
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                rows={4}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">优先级</label>
              <select
                className="rounded-md border bg-background px-3 py-1.5 text-sm"
                value={editPriority}
                onChange={(e) => setEditPriority(e.target.value)}
              >
                {['low', 'normal', 'high', 'urgent'].map((p) => (
                  <option key={p} value={p}>
                    {WORK_ORDER_PRIORITY_LABELS[p] ?? p}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">截止日期</label>
              <Input
                type="date"
                value={editDueAt}
                onChange={(e) => setEditDueAt(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={updateLoading}>
                保存
              </Button>
              <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                取消
              </Button>
            </div>
          </div>
        ) : (
          <dl className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
            <div>
              <dt className="text-muted-foreground">工单类型</dt>
              <dd className="mt-0.5 font-medium">{WORK_ORDER_TYPE_LABELS[workOrder.type] ?? workOrder.type}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">客户 ID</dt>
              <dd className="mt-0.5 font-medium">{workOrder.customerId}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">预约 ID</dt>
              <dd className="mt-0.5 font-medium">{workOrder.appointmentId ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">负责人 ID</dt>
              <dd className="mt-0.5 font-medium">{workOrder.assigneeId ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">截止日期</dt>
              <dd className="mt-0.5 font-medium">
                {workOrder.dueAt ? new Date(workOrder.dueAt).toLocaleDateString('zh-CN') : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">完成时间</dt>
              <dd className="mt-0.5 font-medium">
                {workOrder.completedAt
                  ? new Date(workOrder.completedAt).toLocaleString('zh-CN')
                  : '—'}
              </dd>
            </div>
            <div className="col-span-2">
              <dt className="text-muted-foreground">描述</dt>
              <dd className="mt-0.5 whitespace-pre-wrap">{workOrder.description ?? '—'}</dd>
            </div>
            {workOrder.blockReason && (
              <div className="col-span-2">
                <dt className="text-muted-foreground">阻塞原因</dt>
                <dd className="mt-0.5 whitespace-pre-wrap text-red-600">{workOrder.blockReason}</dd>
              </div>
            )}
            {workOrder.cancelReason && (
              <div className="col-span-2">
                <dt className="text-muted-foreground">取消原因</dt>
                <dd className="mt-0.5 whitespace-pre-wrap text-slate-500">{workOrder.cancelReason}</dd>
              </div>
            )}
            <div>
              <dt className="text-muted-foreground">版本号</dt>
              <dd className="mt-0.5 font-medium">{workOrder.version}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">创建时间</dt>
              <dd className="mt-0.5 font-medium">
                {new Date(workOrder.createdAt).toLocaleString('zh-CN')}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">更新时间</dt>
              <dd className="mt-0.5 font-medium">
                {new Date(workOrder.updatedAt).toLocaleString('zh-CN')}
              </dd>
            </div>
          </dl>
        )}
      </Card>

      {/* Block dialog */}
      {showBlockDialog && (
        <div key={`block-${workOrder.id}-${workOrder.version}`} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <Card className="w-full max-w-md space-y-4 p-6">
            <h3 className="font-semibold">阻塞工单</h3>
            <div>
              <label className="mb-1 block text-sm font-medium">阻塞原因</label>
              <textarea
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                rows={3}
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                placeholder="请输入阻塞原因…"
              />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleBlock}
                disabled={!blockReason.trim() || transLoading}
              >
                确认阻塞
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowBlockDialog(false);
                  setBlockReason('');
                }}
              >
                取消
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Cancel dialog */}
      {showCancelDialog && (
        <div key={`cancel-${workOrder.id}-${workOrder.version}`} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <Card className="w-full max-w-md space-y-4 p-6">
            <h3 className="font-semibold">取消工单</h3>
            <div>
              <label className="mb-1 block text-sm font-medium">取消原因</label>
              <textarea
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="请输入取消原因…"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="destructive"
                size="sm"
                onClick={handleCancel}
                disabled={!cancelReason.trim() || transLoading}
              >
                确认取消
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowCancelDialog(false);
                  setCancelReason('');
                }}
              >
                返回
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Assign dialog */}
      {showAssignDialog && (
        <div key={`assign-${workOrder.id}-${workOrder.version}`} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <Card className="w-full max-w-md space-y-4 p-6">
            <h3 className="font-semibold">指派工单</h3>
            <div>
              <label className="mb-1 block text-sm font-medium">负责人 ID</label>
              <Input
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                placeholder="请输入用户 ID…"
              />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleAssign}
                disabled={!assigneeId.trim() || transLoading}
              >
                确认指派
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowAssignDialog(false);
                  setAssigneeId('');
                }}
              >
                取消
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
